/**
 * Column keys and table configuration for the expandable order-auth worklist table.
 */
import { AuthStatusOptionModel } from "../../data-model/authStatusOptions";
import { getBadgePropsForAuthStatus } from "../../enums/medicalPaOrdersAuthStatus";
import { formatDateToUTCWithFormat } from "../../utils/formatDate";
import { TableCellType, TableHeader } from "../custom-table/table";
import {
  ExpandableTableForOrderAuthTableRow,
  SingleOrderClubbedData,
} from "./types";

/** Row-data and header keys for expandable order-auth table columns. */
export enum ExpandableTableForOrderAuthColumnKeys {
  ORDER_ID = "orderId",
  ORDER_SELECT = "orderSelect",
  /** Row-data key for expand/collapse chevron; must match custom-table `expandableRowIcon`. */
  COLLAPSABLE_SECTION = "expandableRowIcon",
  DATE_OF_SERVICE = "dateOfService",
  REGIMEN_NAME = "regimenName",
  AUTH_STATUS = "authStatus",
  /** Hidden payload forwarded to the nested DataGrid expand panel. */
  AUTH_ON_FILE_DATA = "authOnFileData",
}

/** Stable table name for expandable order-auth CustomTable persistence keys. */
export const EXPANDABLE_ORDER_AUTH_TABLE_NAME = "expandable_order_auth_table";

/** Column headers for the expandable order-auth table; widths sum to 100%. */
export const ExpandableTableForOrderAuthHeaders: TableHeader[] = [
  {
    label: "",
    key: ExpandableTableForOrderAuthColumnKeys.COLLAPSABLE_SECTION,
    order: 0,
    width: 5,
    type: TableCellType.EXPANDABLE_ROW_ICON,
  },
  {
    label: "",
    key: ExpandableTableForOrderAuthColumnKeys.ORDER_SELECT,
    order: 1,
    width: 3,
    type: TableCellType.CHECKBOX,
    hideHeaderCheckbox: true,
  },
  {
    label: "Regimen Name",
    key: ExpandableTableForOrderAuthColumnKeys.REGIMEN_NAME,
    order: 2,
    width: 55,
    type: TableCellType.STRING,
  },
  {
    label: "Date Of Service",
    key: ExpandableTableForOrderAuthColumnKeys.DATE_OF_SERVICE,
    order: 3,
    width: 17,
    type: TableCellType.DATE,
  },
  {
    label: "Auth Status",
    key: ExpandableTableForOrderAuthColumnKeys.AUTH_STATUS,
    order: 4,
    width: 20,
    type: TableCellType.BADGE,
  },
];

/** Options for generating expandable order-auth table rows. */
export interface GenerateExpandableTableForOrderAuthDataOptions {
  /** Order ids that should render as checked in the select column. */
  selectedOrderIds?: string[];
  /**
   * Order id whose collapsible panel should be open.
   * Independent of checkbox multi-select — only one row expands at a time.
   */
  expandedOrderId?: string | null;
  /** When true, row checkboxes are rendered disabled. */
  isSelectionDisabled?: boolean;
}

/**
 * Maps clubbed order/auth-on-file records into custom-table row objects.
 *
 * @param data - Order rows paired with auth-on-file data
 * @param authStatusOptions - Auth status options for badge styling
 * @param options - Checkbox selection, single expanded row id, and disabled state
 * @returns Table row objects keyed by {@link ExpandableTableForOrderAuthColumnKeys}
 */
export const generateExpandableTableForOrderAuthData = (
  data: SingleOrderClubbedData[],
  authStatusOptions: AuthStatusOptionModel[],
  options?: GenerateExpandableTableForOrderAuthDataOptions,
): ExpandableTableForOrderAuthTableRow[] => {
  const selectedOrderIds = new Set(options?.selectedOrderIds ?? []);
  const expandedOrderId = options?.expandedOrderId ?? null;
  const isSelectionDisabled = options?.isSelectionDisabled ?? false;

  return data.map((item): ExpandableTableForOrderAuthTableRow => {
    const orderId = item?.order_id ?? "";
    const detailsForAuthStatusBadge = getBadgePropsForAuthStatus(
      authStatusOptions,
      item?.auth_on_file_data?.auth_status ?? "",
    );

    return {
      [ExpandableTableForOrderAuthColumnKeys.AUTH_ON_FILE_DATA]:
        item?.auth_on_file_data ?? {},
      [ExpandableTableForOrderAuthColumnKeys.ORDER_ID]: orderId,
      [ExpandableTableForOrderAuthColumnKeys.ORDER_SELECT]: {
        id: orderId,
        label: "",
        isChecked: selectedOrderIds.has(orderId),
        isDisabled: isSelectionDisabled,
      },
      [ExpandableTableForOrderAuthColumnKeys.COLLAPSABLE_SECTION]: {
        id: orderId,
        isExpanded: orderId === expandedOrderId,
      },
      [ExpandableTableForOrderAuthColumnKeys.DATE_OF_SERVICE]:
        formatDateToUTCWithFormat(
          item?.order_data?.payload?.date_of_service ?? "",
          "MM/DD/YYYY",
          "--",
        ),
      [ExpandableTableForOrderAuthColumnKeys.REGIMEN_NAME]:
        item?.order_data?.payload?.regimen_name ?? "",
      [ExpandableTableForOrderAuthColumnKeys.AUTH_STATUS]: {
        text: detailsForAuthStatusBadge?.text ?? "",
        color: detailsForAuthStatusBadge?.textColor ?? "",
        bgColor: detailsForAuthStatusBadge?.bgColor ?? "",
      },
    };
  });
};
